import {makeAutoObservable, observable, runInAction} from 'mobx'
import {DeepReadonly} from 'ts-essentials'
import {
  ControlledProcess,
  ControlledProcessStopped,
  Debouncer,
  logger,
  ProcessController,
  processController,
  Scheduler,
  TypeUtil
} from 'yougile-util'
import {DataObjectId, Hub, User} from 'yougile-model'
import {DepartmentsClient} from '@client/departments-client'
import {DirectMessages} from '@client/direct-messages'
import {EventBus} from '@client/event-bus'
import {openChatEvent} from '@client/events'
import {Storage} from '@client/storage'
import {Sync} from '@client/sync'
import {Tracking, TrackingEvent} from '@common/tracking'
import {OptionsItemCheckbox} from '@components/common/base/dropdown-button-menu/types'
import {createUseStore} from '@containers/hooks'
import {TaskChatTab} from '@extensions/app-persistence'
import {Board} from '@extensions/board'
import {ChatUtils} from '@extensions/chat-utils'
import {globals} from '@extensions/globals-base'
import {PageService} from '@extensions/page-service'
import {Project} from '@extensions/project'
import {
  FilterFn,
  PackedId,
  SearchResultItem,
  SearchResultItemType,
  unpackId
} from '@extensions/search'
import {Task} from '@extensions/task'

export type PredefinedFilter =
  | 'everywhere'
  | 'task-titles'
  | 'chat-titles'
  | 'messages'
  | 'current-project'
  | 'custom'

export type FilterCategory =
  | 'task-title'
  | 'user-name'
  | 'group-chat-title'
  | 'message'
  | 'description'
  | 'checklist'

export interface FilterState {
  readonly type: PredefinedFilter
  readonly projectIds: readonly DataObjectId[]
  readonly categories: readonly FilterCategory[]
}

const searchFilterKey = 'search-filter'
export const itemHeight = 56

export const defaultFilterState: DeepReadonly<FilterState> = {
  type: 'current-project',
  projectIds: [],
  categories: []
}

const typeToTab: Partial<Record<SearchResultItemType, TaskChatTab>> = {
  description: 'description',
  'checklist-title': 'checklists',
  'checklist-item': 'checklists'
}

interface State {
  value: string
  debouncedValue: string
  filterState: DeepReadonly<FilterState>
  forceNoLimit: boolean

  inputEl?: HTMLInputElement
  scrollableEl?: HTMLElement
  containerEl?: HTMLElement

  selectedIx: number
  // List items indices to render virtualized list items between
  listFrom: number
  listTo: number
  // Virtualized list page size
  pageSize: number

  inProgress: boolean
  resultsRaw: SearchResultItem[]
  totalCountRaw: number
  resultsFiltered: Map<PredefinedFilter, SearchResultItem[]>

  availablePredefinedFilters: Set<PredefinedFilter>
}

export class SearchStore {
  state: State = {
    value: '',
    debouncedValue: '',
    filterState: defaultFilterState,
    selectedIx: -1,
    listFrom: 0,
    listTo: 0,
    pageSize: 0,
    resultsRaw: [],
    totalCountRaw: 0,
    resultsFiltered: new Map(),
    availablePredefinedFilters: new Set(),
    inProgress: false,
    forceNoLimit: false
  }

  searchProcessController: ProcessController | undefined
  searchDebouncer = new Debouncer(300)
  refreshSearchHandle: number | undefined
  trackingDebouncer = new Debouncer(10000)
  listScrollHandle: number | undefined

  constructor(
    defaultPredefinedFilter: PredefinedFilter,
    availablePredefinedFilters: PredefinedFilter[],
    pageSize: number
  ) {
    makeAutoObservable(
      this,
      {
        state: observable.shallow,
        searchProcessController: observable.ref,
        searchDebouncer: observable.ref,
        refreshSearchHandle: observable.ref,
        trackingDebouncer: observable.ref,
        listScrollHandle: observable.ref
      },
      {autoBind: true}
    )

    this.state.filterState = this.loadFilterState(
      defaultPredefinedFilter,
      availablePredefinedFilters
    )
    this.state.pageSize = pageSize
    this.state.availablePredefinedFilters = new Set(availablePredefinedFilters)
  }

  init(): () => void {
    return () => {
      this.searchProcessController?.stop()
      this.searchDebouncer.clear()
      this.refreshSearchHandle && cancelAnimationFrame(this.refreshSearchHandle)
      this.trackingDebouncer.clear()
      this.listScrollHandle && cancelAnimationFrame(this.listScrollHandle)
    }
  }

  sync(
    defaultPredefinedFilter: PredefinedFilter,
    availablePredefinedFilters: PredefinedFilter[]
  ): void {
    this.state.filterState = this.loadFilterState(
      defaultPredefinedFilter,
      availablePredefinedFilters
    )
    this.state.availablePredefinedFilters = new Set(availablePredefinedFilters)
  }

  stopSearch(): void {
    this.state.value = ''
    this.state.debouncedValue = ''
    this.state.resultsRaw = []
    this.state.totalCountRaw = 0
    this.state.resultsFiltered = new Map()
    this.state.selectedIx = -1
    this.state.listFrom = 0
    this.state.listTo = 2 * this.state.pageSize
    this.state.inputEl?.blur()
    this.state.forceNoLimit = false
  }

  startSearch(): void {
    this.state.selectedIx = -1
    this.state.inputEl?.focus()
    this.state.inputEl?.setSelectionRange(0, this.state.value.length)
  }

  listHome(): boolean {
    if (this.isInputFocused()) {
      return false
    }

    this.state.selectedIx = 0
    this.recalculateLimitsAround(this.state.selectedIx)
    this.scrollToSelected()
    this.hideCursor()
    return true
  }

  listEnd(): boolean {
    if (this.isInputFocused()) {
      return false
    }

    // Scroll to the current bottom limit
    this.state.selectedIx = this.results.length - 1
    this.recalculateLimitsAround(this.state.selectedIx)
    this.scrollToSelected()
    this.hideCursor()
    return true
  }

  listPageUp(): boolean {
    if (this.isInputFocused()) {
      return false
    }

    const height = this.state.scrollableEl?.offsetHeight ?? 0
    const scrollPageSize = Math.floor(height / itemHeight)
    // Scroll a page up, but don't focus an input when at the top
    this.state.selectedIx = Math.max(this.state.selectedIx - scrollPageSize, 0)
    this.scrollToSelected()
    this.hideCursor()
    return true
  }

  listPageDown(): boolean {
    if (this.isInputFocused()) {
      return false
    }

    const height = this.state.scrollableEl?.offsetHeight ?? 0
    const scrollPageSize = Math.floor(height / itemHeight)
    // Scroll a page down, but no more than max number of rows
    this.state.selectedIx = Math.min(
      this.state.selectedIx === -1 ? 0 : this.state.selectedIx + scrollPageSize,
      this.results.length - 1
    )
    this.recalculateLimitsAround(this.state.selectedIx)
    this.scrollToSelected()
    this.hideCursor()
    return true
  }

  listUp(): boolean {
    this.state.selectedIx = Math.max(this.state.selectedIx - 1, -1)
    if (this.state.selectedIx === -1) {
      // Focus an input if an up key is pressed
      this.state.inputEl?.focus()
    } else {
      this.state.inputEl?.blur()
      this.scrollToSelected()
      this.hideCursor()
    }
    return true
  }

  listDown(): boolean {
    this.state.inputEl?.blur()

    this.state.selectedIx = Math.min(
      this.state.selectedIx + 1,
      this.results.length - 1
    )
    this.recalculateLimitsAround(this.state.selectedIx)
    this.scrollToSelected()
    this.hideCursor()
    return true
  }

  listEnter(): boolean {
    const item = this.results[this.state.selectedIx]
    if (!item) {
      return false
    }
    this.itemClick(item.chatId)
    return true
  }

  /**
   * Scrolls a list to a currently selected row.
   */
  private scrollToSelected(): void {
    if (this.state.scrollableEl) {
      if (this.state.selectedIx === -1) {
        this.state.scrollableEl.scrollTop = 0
      } else {
        const {scrollTop, offsetHeight} = this.state.scrollableEl
        const top = this.state.selectedIx * itemHeight
        if (top < scrollTop) {
          // Selected element is higher than the top of the window
          this.state.scrollableEl.scrollTop = top
        } else if (top + itemHeight > scrollTop + offsetHeight) {
          // Selected element is lower than the bottom of the window
          this.state.scrollableEl.scrollTop = top + itemHeight - offsetHeight
        }
      }
    }
  }

  itemHover(ix: number): void {
    this.state.selectedIx = ix
  }

  onListScroll(): void {
    if (!this.listScrollHandle) {
      // Run this no often than each frame
      this.listScrollHandle = requestAnimationFrame(() => {
        this.listScrollHandle = undefined
        if (this.state.scrollableEl) {
          const {scrollTop, offsetHeight} = this.state.scrollableEl
          const ix = Math.floor((scrollTop + offsetHeight / 2) / itemHeight)
          this.recalculateLimitsAround(ix)
        }
      })
    }
  }

  private recalculateLimitsAround(ix: number): void {
    if (this.state.scrollableEl) {
      const pageSize = this.state.pageSize
      // Indexes two pages up&down
      const fromIx = Math.max(ix - pageSize * 2, 0)
      const toIx = Math.min(ix + pageSize * 2, Number.MAX_SAFE_INTEGER)
      // Top&bottom page boundaries that contain from&to indices
      this.state.listFrom = Math.floor(fromIx / pageSize) * pageSize
      this.state.listTo = Math.ceil(toIx / pageSize) * pageSize
    }
  }

  /**
   * Show cursor after hiding only when a user tries to interact with search
   * not using shortcuts. Cursor actually shows only when a mouse is moved.
   */
  showCursor(): void {
    this.state.scrollableEl?.classList.remove('cursor-none')
    this.state.containerEl?.classList.remove('pointer-events-none')
  }

  /**
   * Need to hide a cursor to prevent item selection from mouse hover clashing
   * with item selection from keyboard shortcuts.
   */
  hideCursor(): void {
    this.state.scrollableEl?.classList.add('cursor-none')
    this.state.containerEl?.classList.add('pointer-events-none')
  }

  itemClick(chatId: string): void {
    const {id, messageTime, type} = unpackId(chatId as PackedId)
    this.openChat(id, messageTime, type)
  }

  private openChat(
    id: DataObjectId,
    messageTime?: number,
    type?: SearchResultItemType
  ): void {
    const box = Sync.getCurrentSandbox()
    const obj = box.get(id)
    if (obj instanceof User) {
      const hub = DirectMessages.requestChatWith(id)
      EventBus.dispatch(openChatEvent(hub.id, messageTime, undefined, false))
    } else {
      const tab = type ? typeToTab[type] : undefined
      EventBus.dispatch(openChatEvent(id, messageTime, tab, false))
    }
  }

  setNoLimit(): void {
    this.state.forceNoLimit = true
    this.refreshSearch({resetView: true})
  }

  setValue(value: string): void {
    this.showCursor()
    this.state.value = value
    this.state.forceNoLimit = false
    if (value) {
      this.searchDebouncer.runLatest(() => {
        this.refreshSearch({resetView: true})
      })
    } else {
      // If a value is empty, clean results immediately
      this.refreshSearch({quiet: true, resetView: true})
    }
  }

  setInputEl(el: HTMLInputElement | null | undefined): void {
    if (el) {
      this.state.inputEl = el
    }
  }

  setScrollableEl(el: HTMLElement | null | undefined): void {
    if (el) {
      this.state.scrollableEl = el
    }
  }

  setContainerEl(el: HTMLElement | null | undefined): void {
    if (el) {
      this.state.containerEl = el
    }
  }

  private setFilterState(filterState: FilterState): void {
    this.state.filterState = filterState
    this.saveFilterState(filterState)
  }

  onChangeFilterType(type: PredefinedFilter): void {
    if (this.state.filterState.type === type) {
      return
    }
    this.setFilterState({...this.state.filterState, type})
    // Predefined filter type change doesn't require filtered results recalculation
    this.resetView()
  }

  onChangeFilterProjects(options: OptionsItemCheckbox[]): void {
    const projectIds = options
      .filter(x => x.selected)
      .map(x => x.key) as DataObjectId[]
    this.setFilterState({...this.state.filterState, projectIds})
    this.searchDebouncer.runLatest(() => {
      this.refreshSearch({cacheLevel: 'raw'})
    })
  }

  onChangeFilterCategories(options: OptionsItemCheckbox[]): void {
    const categories = options
      .filter(x => x.selected)
      .map(x => x.key) as FilterCategory[]
    this.setFilterState({...this.state.filterState, categories})
    this.searchDebouncer.runLatest(() => {
      this.refreshSearch({cacheLevel: 'raw'})
    })
  }

  /**
   * Stops previous search process and starts a new one.
   */
  private refreshSearchProcess(): ControlledProcess {
    this.searchProcessController?.stop()
    this.searchProcessController = undefined

    const [controller, process] = processController({
      // No more than 50ms inbetween frames (skips max 3 frames)
      scheduler: Scheduler.frame(),
      maxIterationTime: 50
    })
    this.searchProcessController = controller
    return process
  }

  get results(): SearchResultItem[] {
    const {type} = this.state.filterState
    return this.state.resultsFiltered.get(type) ?? []
  }

  get resultsEveryWhere(): SearchResultItem[] {
    return this.state.resultsFiltered.get('everywhere') ?? []
  }

  /**
   * Refresh all search results (current result list, predefined filter result counts, raw search results).
   * @param opts.resetView Scroll to top of the list and reset selection to input
   * @param opts.quiet Update UI only if something has changed w/o interrupting a user. Doesn't introduce artificial delay.
   * @param opts.cacheLevel `'raw'` means use cached raw results, `'filtered'` means use cached raw&filtered results, `undefined` (default) means don't use cache.
   */
  refreshSearch(opts?: {
    resetView?: boolean
    quiet?: boolean
    cacheLevel?: 'raw' | 'filtered'
  }): void {
    const {resetView = false, quiet = false, cacheLevel} = opts ?? {}
    this.searchDebouncer.clear()
    const process = this.refreshSearchProcess()

    this.state.debouncedValue = this.state.value
    !quiet && (this.state.inProgress = true)

    // Postpone to the next frame so that `inProgress` change is rendered first
    this.refreshSearchHandle = requestAnimationFrame(async () => {
      this.refreshSearchHandle = undefined

      try {
        const {result: raw, totalCount: totalCountRaw} =
          await this.prepareRawResults(process, cacheLevel)
        const filtered = await this.prepareFilteredResults(
          process,
          raw,
          cacheLevel
        )
        await process.checkpoint()

        // Apply state changes in one action so that UI rerenders after search only once
        runInAction(() => {
          this.state.resultsRaw = raw
          this.state.totalCountRaw = totalCountRaw
          this.state.resultsFiltered = filtered

          resetView && this.resetView()
          this.state.inProgress = false
        })

        if (raw.length) {
          this.trackingDebouncer.runNowButNotRightAfter(() =>
            Tracking.send(TrackingEvent.projectSearch)
          )
        }
      } catch (e) {
        // Don't use `finally` because it messes up render order when one process
        // is stopped midway and another is started.
        if (!(e instanceof ControlledProcessStopped)) {
          logger.warn('refresh search:', e)
          this.state.inProgress = false
        }
      }
    })
  }

  private resetView(): void {
    this.state.selectedIx = -1
    this.state.listFrom = 0
    this.state.listTo = 2 * this.state.pageSize
    if (this.state.scrollableEl) {
      this.state.scrollableEl.scrollTop = 0
    }
  }

  private async prepareRawResults(
    process: ControlledProcess,
    cacheLevel?: 'raw' | 'filtered'
  ): Promise<{
    result: SearchResultItem[]
    totalCount: number
  }> {
    if (cacheLevel === 'raw' || cacheLevel === 'filtered') {
      return {
        result: this.state.resultsRaw,
        totalCount: this.state.totalCountRaw
      }
    }

    const value = this.state.value.trim()
    const companyId = globals.getCompanyId()
    const ix = Sync.searchIndices.get(companyId)
    if (!ix || !value.length) {
      return {
        result: [],
        totalCount: 0
      }
    }

    return ix.searchLong(process, value, undefined, this.state.forceNoLimit)
  }

  private async prepareFilteredResults(
    process: ControlledProcess,
    resultsRaw: SearchResultItem[],
    cacheLevel?: 'raw' | 'filtered'
  ): Promise<Map<PredefinedFilter, SearchResultItem[]>> {
    if (cacheLevel === 'filtered') {
      return this.state.resultsFiltered
    }

    const resultsFiltered = new Map<PredefinedFilter, SearchResultItem[]>()
    for (const type of this.state.availablePredefinedFilters) {
      const filterState: FilterState = {...this.state.filterState, type}
      const fn = this.prepareFilterFn(filterState)
      let results = resultsRaw
      if (fn) {
        results = []
        for (const x of resultsRaw) {
          await process.checkpoint()
          fn(x) && results.push(x)
        }
      }
      resultsFiltered.set(type, results)
    }
    return resultsFiltered
  }

  private prepareFilterFn({
    type,
    projectIds,
    categories
  }: FilterState): FilterFn | undefined {
    if (type === 'task-titles') {
      return x => x.type === 'id' || x.type === 'task-title'
    } else if (type === 'chat-titles') {
      return x =>
        x.type === 'id' ||
        x.type === 'task-title' ||
        x.type === 'user-name' ||
        x.type === 'group-chat-title'
    } else if (type === 'messages') {
      return x => x.type === 'message'
    } else if (type === 'current-project') {
      const project = PageService.getCurrentProject()
      return this.prepareCustomFilterFn([project.id], [])
    } else if (type === 'custom') {
      return this.prepareCustomFilterFn(projectIds, categories)
    }
    return undefined
  }

  private prepareCustomFilterFn(
    projectIds: readonly DataObjectId[],
    categories: readonly FilterCategory[]
  ): (item: SearchResultItem) => boolean {
    const box = Sync.getCurrentSandbox()
    const hierarchy = DepartmentsClient.getHierarchy(box)

    const allowedLocations = new Set<DataObjectId>()
    const projects = projectIds
      .map(id => box.getHub(id))
      .filter(TypeUtil.isTruthy)
    projects.forEach(project => {
      Project.listBoards(project).forEach(board => {
        Board.listColumns(board, {hierarchy}).forEach(x => {
          allowedLocations.add(x.id)
        })
      })
    })

    return ({entity, type}) => {
      if (entity instanceof Hub) {
        const kind = ChatUtils.getChatKind(entity)

        // Only task hubs are checked for location, group and other chats are always present
        let locationMatch = true
        if (kind === 'task' && projectIds.length) {
          const allParentIds = Task.getAllColumnLevelParentTaskIds(
            box,
            entity.id
          )
          locationMatch = allParentIds.some(parentId => {
            const hub = box.getHub(parentId)
            return hub ? allowedLocations.has(hub.location) : false
          })
        }

        let categoryMatch = true
        if (categories.length) {
          categoryMatch = categories.some(category => {
            if (category === 'checklist') {
              return type === 'checklist-title' || type === 'checklist-item'
            } else if (type === 'id' && category === 'task-title') {
              return true
            } else {
              return category === type
            }
          })
        }

        return locationMatch && categoryMatch
      } else if (entity instanceof User) {
        if (categories.length) {
          // Matches 'user-name' and 'message'
          return categories.some(category => category === type)
        }
        return true
      } else {
        return false
      }
    }
  }

  private loadFilterState(
    defaultPredefinedFilter: PredefinedFilter,
    availablePredefinedFilters: PredefinedFilter[]
  ): FilterState {
    const key = this.prepareFilterStateKey()
    let filterState: FilterState | undefined = Storage.getItemAsJson(key)
    if (!filterState) {
      filterState = {...defaultFilterState, type: defaultPredefinedFilter}
    }
    if (
      availablePredefinedFilters?.length &&
      !availablePredefinedFilters.includes(filterState.type)
    ) {
      filterState = {...filterState, type: defaultPredefinedFilter}
    }
    return filterState
  }

  private saveFilterState(filterState: FilterState): void {
    const key = this.prepareFilterStateKey()
    Storage.setItemAsJson(key, filterState)
  }

  private prepareFilterStateKey(): string {
    const companyId = globals.getCompanyId()
    return searchFilterKey + '-' + companyId
  }

  private isInputFocused(): boolean {
    return document.activeElement === this.state.inputEl
  }
}

export const [SearchContext, useSearchStore] = createUseStore(SearchStore)
